import { motion } from "framer-motion";
import RevealText from "@/components/shared/RevealText";
import { ScrollReveal, StaggerParent, StaggerItem } from "@/components/shared/ScrollReveal";
import { useLang } from "@/i18n/LanguageContext";
import Numbers from "@/components/home/Numbers";
import FinalCta from "@/components/home/FinalCta";
import SEO from "@/components/shared/SEO";

const IMG =
  `${process.env.PUBLIC_URL}/images/group.jpg`;

export default function GroupPage() {
  const { t } = useLang();
  return (
    <main data-testid="group-page" className="bg-black text-white">
      <SEO title={t.seo.group.title} description={t.seo.group.desc} />
      <section className="relative pt-40 md:pt-56 pb-24 md:pb-32 border-b border-white/10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-8">{t.group.eyebrow}</p>
          <RevealText
            lines={t.group.title.split("\n")}
            className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-[112px] leading-[0.9] tracking-tighter font-medium"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-10 max-w-2xl text-neutral-300 text-lg md:text-xl leading-relaxed"
          >
            {t.group.body}
          </motion.p>
        </div>
      </section>

      <section className="relative py-24 md:py-32 border-b border-white/10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <ScrollReveal className="md:col-span-5 md:sticky md:top-32 self-start">
            <div className="clip-frame overflow-hidden aspect-[4/5] max-w-md">
              <img src={IMG} alt="" className="w-full h-full object-cover spotlight-img" />
            </div>
          </ScrollReveal>
          <div className="md:col-span-7">
            <StaggerParent className="border-t border-white/10">
              {t.group.timeline.map((tl, i) => (
                <StaggerItem key={tl.year}>
                  <div
                    data-testid={`timeline-${tl.year}`}
                    className="grid grid-cols-12 gap-4 py-8 border-b border-white/10 group"
                  >
                    <div className="col-span-3 md:col-span-2">
                      <p className="font-display text-2xl md:text-3xl text-[var(--dp-accent)]">
                        {tl.year}
                      </p>
                    </div>
                    <div className="col-span-9 md:col-span-10">
                      <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-white mb-2">
                        {tl.t}
                      </h3>
                      <p className="text-neutral-400 text-base leading-relaxed max-w-xl">{tl.d}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerParent>
          </div>
        </div>
      </section>

      <section className="relative py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <ScrollReveal>
            <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-6">Valori</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-16 max-w-3xl">
              Tre principi che guidano ogni progetto.
            </h2>
          </ScrollReveal>
          <StaggerParent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.group.values.map((v, i) => (
              <StaggerItem key={i}>
                <div className="tile-card p-8 md:p-10 h-full">
                  <p className="font-mono-eyebrow text-neutral-500 mb-6">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-display text-3xl md:text-4xl leading-none tracking-tight mb-4">
                    {v.t}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">{v.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerParent>
        </div>
      </section>

      <Numbers />
      <FinalCta />
    </main>
  );
}
